'use client';

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import userApi from '../../services/admin/userApi';
import { 
  Users, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock,
  User,
  Mail,
  Calendar,
  Shield,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Lock,
  Unlock
} from 'lucide-react';

const UserCRUD = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    role_id: ''
  });
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  // Load data khi component mount
  useEffect(() => {
    loadUsers();
  }, [token, currentPage, pageSize]);

  // Filter users khi search hoặc filter thay đổi
  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, statusFilter, roleFilter]);

  // Load users từ API với pagination
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Tạo params cho API
      const params = {
        page: currentPage,
        limit: pageSize
      };

      // Thêm filters nếu có
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (roleFilter !== 'all') {
        params.roleId = roleFilter;
      }
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      
      const response = await userApi.getUsers(token, params);
      
      if (response.data && response.data.success) {
        setUsers(response.data.data || []);
        setPagination(response.data.pagination || {
          page: 1,
          limit: pageSize,
          total: 0,
          totalPages: 1
        });
      } else {
        throw new Error('API response error');
      }
      
    } catch (error) {
      console.error('Error loading users:', error);
      setError(`Lỗi khi tải dữ liệu: ${error.message}`);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter users theo search và filter (client-side cho UI)
  const filterUsers = () => {
    let filtered = [...users];
    
    // Filter theo search term (client-side)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.role_name?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter theo status (client-side)
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }
    
    // Filter theo role (client-side)
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role_name === roleFilter);
    }
    
    setFilteredUsers(filtered);
  };

  // Handle search với debounce
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset về trang đầu khi search
  };

  // Handle status filter
  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset về trang đầu khi filter
  };

  // Handle role filter
  const handleRoleFilter = (e) => {
    setRoleFilter(e.target.value);
    setCurrentPage(1); // Reset về trang đầu khi filter
  };

  // Handle page size change
  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(1); // Reset về trang đầu khi thay đổi page size
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle refresh
  const handleRefresh = async () => {
    setCurrentPage(1);
    setSearchTerm('');
    setStatusFilter('all');
    setRoleFilter('all');
    await loadUsers();
    toast.success('Đã làm mới dữ liệu');
  };

  // Handle user click để xem chi tiết
  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowUserDetail(true);
  };

  // Handle edit user
  const handleEditUser = (user) => {
    // Không cho phép sửa admin
    if (user.role_name === 'admin') {
      toast.error('Không thể chỉnh sửa tài khoản Admin!');
      return;
    }
    
    setEditingUser(user);
    setEditForm({
      role_id: user.role_id || ''
    });
    setShowEditModal(true);
  };

  // Handle edit form change
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const roleId = parseInt(editForm.role_id);
      const response = await userApi.changeUserRole(token, editingUser.id, roleId);
      
      if (response.data && response.data.success) {
        toast.success('Cập nhật vai trò thành công!');
        setShowEditModal(false);
        setEditingUser(null);
        setEditForm({
          role_id: ''
        });
        // Reload users
        await loadUsers();
      } else {
        throw new Error('API response error');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(`Lỗi khi cập nhật vai trò: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setEditForm({
      role_id: ''
    });
  };

  // Handle lock/unlock account
  const handleToggleAccountStatus = async (user) => {
    // Không cho phép khóa admin
    if (user.role_name === 'admin') {
      toast.error('Không thể khóa tài khoản Admin!');
      return;
    }

    const action = user.status === 'suspended' ? 'mở khóa' : 'khóa';
    const confirmMessage = `Bạn có chắc chắn muốn ${action} tài khoản của ${user.name}?`;

    if (window.confirm(confirmMessage)) {
      try {
        setLoading(true);
        const token = sessionStorage.getItem('token');
        
        // Nếu đang suspended thì mở khóa (approved), nếu không thì khóa (suspended)
        const newStatus = user.status === 'suspended' ? 'approved' : 'suspended';
        // Giữ nguyên roleId hiện tại của user
        const currentRoleId = user.role_id || 2; // Default là 2 (Seller) nếu không có
        
        const response = await userApi.approveUser(token, user.id, newStatus, currentRoleId);
        
        if (response.data && response.data.success) {
          toast.success(`Đã ${action} tài khoản thành công!`);
          await loadUsers(); // Reload danh sách
        } else {
          throw new Error('API response error');
        }
      } catch (error) {
        console.error('Error toggling account status:', error);
        toast.error(`Lỗi khi ${action} tài khoản: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'waiting_approval': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'locked': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved': return 'Đã duyệt';
      case 'waiting_approval': return 'Chờ duyệt';
      case 'rejected': return 'Từ chối';
      case 'suspended': return 'Đã khóa';
      case 'locked': return 'Đã khóa';
      default: return status;
    }
  };

  // Get role color
  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 'seller': return 'bg-blue-100 text-blue-800';
      case 'designer': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get role label
  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'seller': return 'Seller';
      case 'designer': return 'Designer';
      default: return role || 'Chưa phân quyền';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Generate pagination buttons
  const generatePaginationButtons = () => {
    const buttons = [];
    const { page, totalPages } = pagination;
    
    // Previous button
    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(page - 1)}
        disabled={page <= 1}
        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
    );

    // First page
    if (page > 3) {
      buttons.push(
        <button
          key="first"
          onClick={() => handlePageChange(1)}
          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
        >
          1
        </button>
      );
      if (page > 4) {
        buttons.push(
          <span key="dots1" className="px-3 py-2 text-sm text-gray-500">
            ...
          </span>
        );
      }
    }

    // Current page and surrounding pages
    for (let i = Math.max(1, page - 1); i <= Math.min(totalPages, page + 1); i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-2 text-sm font-medium border border-gray-300 ${
            i === page
              ? 'bg-blue-600 text-white border-blue-600'
              : 'text-gray-500 bg-white hover:bg-gray-50'
          }`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (page < totalPages - 2) {
      if (page < totalPages - 3) {
        buttons.push(
          <span key="dots2" className="px-3 py-2 text-sm text-gray-500">
            ...
          </span>
        );
      }
      buttons.push(
        <button
          key="last"
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(page + 1)}
        disabled={page >= totalPages}
        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    );

    return buttons;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col w-[100vw] lg:w-[80vw] mx-auto overflow-hidden">
      {/* Fixed Header - Không bao giờ di chuyển */}
      <div className="bg-white rounded-lg shadow-lg mb-4 sm:mb-6 w-[100vw] lg:w-[80vw] mx-auto flex-shrink-0">
        {/* Title and Actions */}
        <div className="p-3 sm:p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900">Quản lý người dùng</h1>
              <p className="text-sm text-gray-600 mt-1">Quản lý tài khoản và phân quyền</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Làm mới</span>
              </button>
              <button className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors flex items-center gap-2">
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Thêm user</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="p-3 sm:p-4 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Tìm kiếm user..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={handleStatusFilter}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="approved">Đã duyệt</option>
              <option value="waiting_approval">Chờ duyệt</option>
              <option value="rejected">Từ chối</option>
            </select>

            {/* Role Filter */}
            <select
              value={roleFilter}
              onChange={handleRoleFilter}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tất cả vai trò</option>
              <option value="admin">Admin</option>
              <option value="seller">Seller</option>
              <option value="designer">Designer</option>
            </select>

            {/* Page Size */}
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={10}>10 / trang</option>
              <option value={50}>50 / trang</option>
              <option value={100}>100 / trang</option>
            </select>

            {/* Results count */}
            <div className="flex items-center justify-end text-sm text-gray-600">
              {pagination.total} kết quả
            </div>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 p-4 rounded-xl mb-4">
          <p className="text-red-600">{error}</p>
          <button 
            onClick={loadUsers}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Thử lại
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-lg flex-1 flex flex-col overflow-hidden min-h-0">
        {/* Fixed Table Header - Không bao giờ di chuyển */}
        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex-shrink-0">
          <div className="grid grid-cols-5 gap-4">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">User</div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Vai trò</div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Trạng thái</div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Ngày tạo</div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Thao tác</div>
          </div>
        </div>
        
        {/* Scrollable Table Body - Chỉ phần này scroll */}
        <div className="overflow-y-auto min-h-0 h-[800px]">
          <table className="min-w-full divide-y divide-gray-200">
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleUserClick(user)}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img 
                          className="h-10 w-10 rounded-full object-cover" 
                          src={user.avatar || '/images/default-avatar.svg'} 
                          alt={user.name}
                          onError={(e) => {
                            e.target.src = '/images/default-avatar.svg';
                          }}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role_name)}`}>
                      {getRoleLabel(user.role_name)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.status)}`}>
                      {getStatusLabel(user.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUserClick(user);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Xem chi tiết"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditUser(user);
                        }}
                        disabled={user.role_name === 'admin'}
                        className={`p-1 ${
                          user.role_name === 'admin' 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-green-600 hover:text-green-900'
                        }`}
                        title={user.role_name === 'admin' ? 'Không thể chỉnh sửa Admin' : 'Chỉnh sửa'}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleAccountStatus(user);
                        }}
                        disabled={user.role_name === 'admin'}
                        className={`p-1 ${
                          user.role_name === 'admin' 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : user.status === 'locked'
                              ? 'text-orange-600 hover:text-orange-900'
                              : 'text-red-600 hover:text-red-900'
                        }`}
                        title={user.role_name === 'admin' 
                          ? 'Không thể khóa Admin' 
                          : user.status === 'locked' 
                            ? 'Mở khóa tài khoản' 
                            : 'Khóa tài khoản'
                        }
                      >
                        {user.status === 'locked' ? (
                          <Unlock className="w-4 h-4" />
                        ) : (
                          <Lock className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Trước
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sau
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Hiển thị{' '}
                  <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>
                  {' '}đến{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>
                  {' '}trong tổng số{' '}
                  <span className="font-medium">{pagination.total}</span>
                  {' '}kết quả
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  {generatePaginationButtons()}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {showUserDetail && selectedUser && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowUserDetail(false)}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <User className="w-6 h-6 text-blue-600" />
                  <h3 className="text-xl font-bold text-gray-900">Chi tiết người dùng</h3>
                </div>
                <button
                  onClick={() => setShowUserDetail(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* User Info */}
                <div className="flex items-center space-x-4">
                  <img 
                    className="h-16 w-16 rounded-full object-cover" 
                    src={selectedUser.avatar || '/images/default-avatar.svg'} 
                    alt={selectedUser.name}
                    onError={(e) => {
                      e.target.src = '/images/default-avatar.svg';
                    }}
                  />
                  <div>
                    <h4 className="text-lg font-bold text-gray-900">{selectedUser.name}</h4>
                    <p className="text-gray-600">{selectedUser.email}</p>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Shield className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Vai trò</span>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(selectedUser.role_name)}`}>
                      {getRoleLabel(selectedUser.role_name)}
                    </span>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Trạng thái</span>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedUser.status)}`}>
                      {getStatusLabel(selectedUser.status)}
                    </span>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Calendar className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Ngày tạo</span>
                    </div>
                    <p className="text-sm text-gray-600">{formatDate(selectedUser.created_at)}</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Mail className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">ID Google</span>
                    </div>
                    <p className="text-sm text-gray-600">{selectedUser.id_google || 'Không có'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={handleCancelEdit}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <Edit className="w-6 h-6 text-green-600" />
                  <h3 className="text-xl font-bold text-gray-900">Chỉnh sửa vai trò</h3>
                </div>
                <button
                  onClick={handleCancelEdit}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* User Info */}
                <div className="flex items-center space-x-4 mb-6">
                  <img 
                    className="h-12 w-12 rounded-full object-cover" 
                    src={editingUser.avatar || '/images/default-avatar.svg'} 
                    alt={editingUser.name}
                    onError={(e) => {
                      e.target.src = '/images/default-avatar.svg';
                    }}
                  />
                  <div>
                    <h4 className="text-lg font-bold text-gray-900">{editingUser.name}</h4>
                    <p className="text-gray-600">{editingUser.email}</p>
                  </div>
                </div>

                {/* Edit Form */}
                <div className="space-y-4">
                  {/* Current Role Display */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Shield className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Vai trò hiện tại</span>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(editingUser.role_name)}`}>
                      {getRoleLabel(editingUser.role_name)}
                    </span>
                  </div>

                  {/* Role Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Chọn vai trò mới
                    </label>
                    <select
                      name="role_id"
                      value={editForm.role_id}
                      onChange={handleEditFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Chọn vai trò</option>
                      <option value="2">Seller</option>
                      <option value="3">Designer</option>
                      <option value="1">Admin</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Hủy
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Đang lưu...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Lưu thay đổi</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserCRUD; 
